import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { categories } from "@/mocks/categories";
import { formatPrice } from "@/lib/catalog";

interface SearchProduct {
  id: number;
  title: string;
  price: number | string;
  currency: string | null;
  category: string | null;
  images: string[] | null;
  views: number | null;
}

interface SmartSearchProps {
  variant?: "hero" | "compact";
  onNavigate?: () => void;
}

/**
 * Buscador inteligente / recomendador perpetuo.
 * Muestra etiquetas de tendencia cuando está vacío y sugerencias
 * predictivas + micro-recomendaciones instantáneas mientras se escribe.
 */
export default function SmartSearch({
  variant = "hero",
  onNavigate,
}: SmartSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState<SearchProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    supabase
      .from("products")
      .select("id, title, price, currency, category, images, views")
      .eq("status", "active")
      .order("views", { ascending: false })
      .limit(30)
      .then(({ data, error }) => {
        if (!mounted) return;
        if (!error) setProducts((data ?? []) as SearchProduct[]);
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const trimmed = query.trim().toLowerCase();

  const matchedProducts = useMemo(() => {
    if (!trimmed) return [];
    return products.filter((p) =>
      p.title.toLowerCase().includes(trimmed)
    );
  }, [products, trimmed]);

  const matchedCategories = useMemo(() => {
    if (!trimmed) return [];
    return categories.filter((c) =>
      c.name.toLowerCase().includes(trimmed)
    );
  }, [trimmed]);

  const recommended = useMemo(
    () => products.slice(0, 5),
    [products]
  );

  // Tendencias reales: los anuncios más vistos. Si aún no hay publicaciones,
  // se muestran las categorías para no dejar el buscador vacío.
  const trending = useMemo(() => {
    const fromProducts = products.slice(0, 6).map((p) => p.title);
    return fromProducts.length > 0
      ? fromProducts
      : categories.slice(0, 6).map((c) => c.name);
  }, [products]);

  const goToProduct = (id: number) => {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    navigate(`/producto/${id}`);
  };

  const goToCategory = (slug: string) => {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    navigate(`/categoria/${slug}`);
  };

  const pickTrend = (term: string) => {
    setQuery(term);
    setOpen(true);
  };

  const isHero = variant === "hero";

  return (
    <div ref={boxRef} className="relative w-full">
      {/* Barra de búsqueda */}
      <div
        className={`glass-liquid flex items-center gap-2 rounded-full transition-shadow ${
          isHero
            ? "px-3 py-2 md:px-4 md:py-2.5"
            : "px-3 py-1.5"
        } ${open ? "shadow-lift" : ""}`}
      >
        <i
          className={`relative z-10 ri-search-line ${
            isHero ? "pl-1 text-lg" : "text-base"
          } text-foreground-500`}
        ></i>
        <input
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={
            isHero
              ? "¿Qué estás buscando hoy?"
              : "Buscar productos..."
          }
          className="relative z-10 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Limpiar búsqueda"
            className="relative z-10 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100/70 hover:text-foreground-700"
          >
            <i className="ri-close-line"></i>
          </button>
        )}
        {isHero && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="relative z-10 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 bg-gradient-to-b from-white/25 to-transparent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-95"
          >
            Buscar
          </button>
        )}
      </div>

      {/* Panel desplegable */}
      {open && (
        <div className="glass-strong absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl shadow-lift animate-pop-in">
          {!trimmed ? (
            <div className="p-4">
              {/* Tendencias */}
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                <i className="ri-fire-line text-accent-500"></i>
                Tendencias
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {trending.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => pickTrend(term)}
                    className="cursor-pointer whitespace-nowrap rounded-full border border-background-200 bg-background-100/70 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                  >
                    {term}
                  </button>
                ))}
              </div>

              {/* Recomendados */}
              <p className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                <i className="ri-sparkling-2-line text-primary-500"></i>
                Recomendados para ti
              </p>
              {loading ? (
                <div className="mt-3 space-y-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={index}
                      className="flex animate-pulse items-center gap-3"
                    >
                      <div className="h-11 w-11 rounded-md bg-background-200" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 w-2/3 rounded bg-background-200" />
                        <div className="h-3 w-1/3 rounded bg-background-200" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recommended.length === 0 ? (
                <p className="mt-3 text-sm text-foreground-500">
                  Aún no hay productos destacados.
                </p>
              ) : (
                <div className="mt-2 divide-y divide-background-200/60">
                  {recommended.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => goToProduct(p.id)}
                      className="flex w-full cursor-pointer items-center gap-3 py-2.5 text-left transition-colors hover:bg-background-100/60"
                    >
                      <span className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-background-200">
                        {p.images?.[0] ? (
                          <img
                            src={p.images[0]}
                            alt={p.title}
                            className="h-full w-full object-cover object-top"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-foreground-300">
                            <i className="ri-image-line"></i>
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground-900">
                          {p.title}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {formatPrice(p.price, p.currency)}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : matchedProducts.length === 0 && matchedCategories.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <p className="mt-2 text-sm text-foreground-500">
                Sin resultados para “{query}”
              </p>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {matchedCategories.length > 0 && (
                <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  Categorías
                </p>
              )}
              {matchedCategories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => goToCategory(c.id)}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${c.icon} text-base`}></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-900">
                    {c.name}
                  </span>
                </button>
              ))}

              {matchedProducts.length > 0 && (
                <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  Productos
                </p>
              )}
              {matchedProducts.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => goToProduct(p.id)}
                  className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <span className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-background-200">
                    {p.images?.[0] ? (
                      <img
                        src={p.images[0]}
                        alt={p.title}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-foreground-300">
                        <i className="ri-image-line"></i>
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-900">
                      {p.title}
                    </span>
                    <span className="block text-xs text-foreground-500">
                      {formatPrice(p.price, p.currency)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}